/**
 * =============================================================================
 * reveal.js — We Are Rewind
 * =============================================================================
 * Apparition des sections au scroll, sur tous les écrans. Le rendu est en CSS
 * (snippets/global_styles.liquid) ; ce script ne fait que poser la classe
 * .is-revealed quand un élément entre à l'écran.
 *
 *   data-reveal            → l'élément apparaît ;
 *   data-reveal-children   → chacun de ses enfants directs apparaît.
 *   (La valeur "load" est gérée en CSS seul, au chargement : ignorée ici.)
 *
 * Les éléments qui entrent à l'écran en même temps apparaissent en cascade
 * (STAGGER s entre chacun, plafonné à MAX_DELAY). Chaque élément n'apparaît
 * qu'une fois. Les éléments ajoutés plus tard (filtres de collection, rendus
 * Liquify…) sont pris en charge automatiquement.
 *
 * Sans la classe has-reveal sur <html> (éditeur de thème, « réduire les
 * animations », navigateur sans IntersectionObserver), rien n'est masqué et le
 * script ne fait rien.
 * =============================================================================
 */
(function () {
  'use strict';

  window.WARReveal = true;
  const root = document.documentElement;
  if (!root.classList.contains('has-reveal')) return;

  const ITEM = '[data-reveal]:not([data-reveal="load"])';
  const PARENT = '[data-reveal-children]:not([data-reveal-children="load"])';
  const STAGGER = 0.1;
  const MAX_DELAY = 0.5;

  /* Un élément qui contient un déclencheur ScrollTrigger (ex. parallaxe collab)
     est décalé pendant son apparition : on recalcule les positions à la fin. */
  let refreshTimer = null;
  function refreshScrollTriggers(el) {
    const ST = window.ScrollTrigger;
    if (!ST || !ST.getAll().some((st) => st.trigger && el.contains(st.trigger))) return;
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => ST.refresh(), 100);
  }
  document.addEventListener('animationend', (e) => {
    if (e.animationName === 'reveal-fade-up' && e.target instanceof Element) refreshScrollTriggers(e.target);
  });

  const pending = new Set();

  function show(el, delay, instant) {
    pending.delete(el);
    observer.unobserve(el);
    el.style.setProperty('--reveal-delay', Math.round(delay * 1000) + 'ms');
    el.classList.add('is-revealed');
    if (instant) el.classList.add('is-instant');
  }

  /* Les éléments animés au chargement en CSS (data-reveal="load" : titre du
     hero…) passent en premier : ce qui apparaît via ce script pendant que ces
     animations n'ont pas toutes commencé attend la dernière, + STAGGER. Ex. la
     grille d'une collection, visible dès l'arrivée, suit le titre au lieu de le
     précéder. Instant (horloge du document, en ms) où démarre la dernière : */
  const loadEnd = document
    .getAnimations()
    .filter((a) => a.animationName === 'reveal-fade-up')
    .reduce((end, a) => {
      const start = a.startTime === null ? document.timeline.currentTime : a.startTime;
      return Math.max(end, start + a.effect.getTiming().delay);
    }, 0);
  function afterLoadAnimations() {
    const remaining = (loadEnd - document.timeline.currentTime) / 1000;
    return remaining > 0 ? remaining + STAGGER : 0;
  }

  // Ordre de lecture : de haut en bas, puis de gauche à droite.
  function revealBatch(els) {
    const base = afterLoadAnimations();
    els
      .map((el) => ({ el, rect: el.getBoundingClientRect() }))
      .sort((a, b) => a.rect.top - b.rect.top || a.rect.left - b.rect.left)
      .forEach(({ el }, i) => show(el, base + Math.min(i * STAGGER, MAX_DELAY)));
  }

  const observer = new IntersectionObserver(
    (entries) => {
      const entering = [];
      entries.forEach((entry) => {
        if (entry.isIntersecting) entering.push(entry.target);
        // Déjà dépassé (rechargement au milieu de la page) : affiché sans animation.
        else if (entry.boundingClientRect.bottom < 0) show(entry.target, 0, true);
      });
      revealBatch(entering);
    },
    { rootMargin: '0px 0px -10% 0px' }
  );

  /* Filet de sécurité, sans attendre l'IntersectionObserver : au démarrage (ce
     qui est déjà à l'écran apparaît tout de suite), au redimensionnement, et au
     scroll — en scroll rapide ou en sautant vers une ancre, un élément peut
     traverser l'écran entre deux passages de l'observer sans être signalé. */
  let checkQueued = false;
  function checkPending() {
    checkQueued = false;
    const limit = window.innerHeight * 0.9;
    const entering = [];
    pending.forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (!rect.width && !rect.height) return; // masqué (display: none)
      if (rect.bottom < 0) show(el, 0, true);
      else if (rect.top < limit) entering.push(el);
    });
    revealBatch(entering);
  }
  function queueCheck() {
    if (checkQueued || !pending.size) return;
    checkQueued = true;
    setTimeout(checkPending, 100);
  }
  window.addEventListener('scroll', queueCheck, { passive: true });
  window.addEventListener('resize', queueCheck);

  function watch(el) {
    if (el.classList.contains('is-revealed') || pending.has(el)) return;
    pending.add(el);
    observer.observe(el);
  }

  function scan(node) {
    if (!(node instanceof Element)) return;
    if (node.matches(ITEM)) watch(node);
    if (node.parentElement && node.parentElement.matches(PARENT)) watch(node);
    if (node.matches(PARENT)) Array.from(node.children).forEach(watch);
    node.querySelectorAll(ITEM).forEach(watch);
    node.querySelectorAll(PARENT).forEach((parent) => Array.from(parent.children).forEach(watch));
  }

  scan(document.body);
  checkPending();
  new MutationObserver((mutations) => {
    mutations.forEach((m) => m.addedNodes.forEach(scan));
    queueCheck();
  }).observe(document.body, { childList: true, subtree: true });
})();
